import { InAppMessageContextMessage, InAppMessageExperimentContext } from "../../../../model/model"
import ObjectUtil from "../../../../util/ObjectUtil"
import { EvaluatorContext, EvaluatorRequest } from "../../../evalautor/Evaluator"
import { EvaluationEventRecorder } from "../../EvaluationEventRecorder"
import { InAppMessageEvaluator } from "../InAppMessageEvaluator"
import { InAppMessageExperimentEvaluator } from "./InAppMessageExperimentEvaluator"
import { InAppMessageLayoutEvaluation } from "./InAppMessageLayoutEvaluation"
import { InAppMessageLayoutRequest } from "./InAppMessageLayoutRequest"
import { InAppMessageLayoutSelector } from "./InAppMessageLayoutSelector"

export class InAppMessageLayoutEvaluator extends InAppMessageEvaluator<
  InAppMessageLayoutRequest,
  InAppMessageLayoutEvaluation
> {
  constructor(
    private readonly experimentEvaluator: InAppMessageExperimentEvaluator,
    private readonly selector: InAppMessageLayoutSelector,
    private readonly eventRecorder: EvaluationEventRecorder
  ) {
    super()
  }

  /**
   * @override
   */
  supports(request: EvaluatorRequest): request is InAppMessageLayoutRequest {
    return request instanceof InAppMessageLayoutRequest
  }

  /**
   * @override
   */
  evaluateInternal(request: InAppMessageLayoutRequest, context: EvaluatorContext): InAppMessageLayoutEvaluation {
    const experimentContext = request.inAppMessage.messageContext.experimentContext
    const message =
      experimentContext !== null
        ? this.evaluateExperiment(request, context, experimentContext)
        : this.evaluateDefault(request, context)
    return InAppMessageLayoutEvaluation.of(request, context, message)
  }

  private evaluateDefault(request: InAppMessageLayoutRequest, context: EvaluatorContext): InAppMessageContextMessage {
    const langCondition = LangCondition(request.inAppMessage.messageContext.defaultLang)
    return this.selector.select(request.inAppMessage, langCondition)
  }

  private evaluateExperiment(
    request: InAppMessageLayoutRequest,
    context: EvaluatorContext,
    experimentContext: InAppMessageExperimentContext
  ): InAppMessageContextMessage {
    const experiment = ObjectUtil.requiredNotNullOrUndefined(
      request.workspace.getExperimentOrNull(experimentContext.key),
      () => `Experiment[key=${experimentContext.key}]`
    )
    const experimentEvaluation = this.experimentEvaluator.evaluate(request, context, experiment)

    const langCondition = LangCondition(request.inAppMessage.messageContext.defaultLang)
    const experimentCondition = ExperimentCondition(experimentEvaluation.variationKey)
    return this.selector.select(
      request.inAppMessage,
      (message) => langCondition(message) && experimentCondition(message)
    )
  }

  /**
   * @override
   */
  record(request: InAppMessageLayoutRequest, evaluation: InAppMessageLayoutEvaluation): void {
    this.eventRecorder.record(request, evaluation)
  }
}

function LangCondition(lang: string) {
  return (message: InAppMessageContextMessage) => lang === message.lang
}

function ExperimentCondition(variationKey: string) {
  return (message: InAppMessageContextMessage) => variationKey === message.variationKey
}
